import React, { useCallback, useEffect, useState } from 'react';

interface Props {
  /** images to show; null/empty closes the lightbox */
  images: string[] | null;
  onClose: () => void;
}

const Lightbox: React.FC<Props> = ({ images, onClose }) => {
  const imgs = images || [];
  const [idx, setIdx] = useState(0);
  const open = imgs.length > 0;

  useEffect(() => { setIdx(0); }, [images]);

  // lock body scroll while open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [open]);

  const nav = useCallback((d: number) => {
    const l = imgs.length;
    if (l) setIdx((i) => (i + d + l) % l);
  }, [imgs.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') nav(-1);
      else if (e.key === 'ArrowRight') nav(1);
      else if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, nav, onClose]);

  if (!open) return null;
  const multi = imgs.length > 1;

  return (
    <div id="lightbox" className="open" role="dialog" aria-modal="true" aria-label="Image gallery" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <button className="lb-close" type="button" onClick={onClose} aria-label="Close">×</button>
      {multi && <button className="lb-nav" id="lbPrev" type="button" onClick={() => nav(-1)} aria-label="Previous">&#8249;</button>}
      <img id="lbImg" src={imgs[idx]} alt="" decoding="async" key={idx} />
      <div id="lbCaption">{idx + 1} / {imgs.length}</div>
      <div className="lb-dots" id="lbDots">
        {imgs.map((_, i) => (
          <button
            key={i}
            type="button"
            className={`lb-dot${i === idx ? ' active' : ''}`}
            aria-label={`Go to image ${i + 1}`}
            aria-current={i === idx}
            onClick={() => setIdx(i)}
          />
        ))}
      </div>
      {multi && <button className="lb-nav" id="lbNext" type="button" onClick={() => nav(1)} aria-label="Next">&#8250;</button>}
    </div>
  );
};

export default Lightbox;
